// aldoo.com checkout steps, registered in the aldoo-ra Store step registry.
// The flow (components/Store/flows/aldoo.jsx) lists them by id; each step's
// `condition` decides from the cart whether it is shown.
import { registerStep } from "aldoo-ra/Store/StorePurchaseFlow"
import { hasPhysicalItems, isDigitalOnly, linesNeedingPlatform } from "./cartItems"
import { INVOICE_REQUEST } from "./payment"
import { flowT } from "./flowTexts"

const t = (text) => () => flowT(text)

/**
 * Whether the buyer asked for an invoice in this checkout: "Need an invoice?"
 * on the payment step, or the address step's checkbox. Both live in the flow's
 * step data, which is cleared when the checkout closes, so a request from an
 * earlier checkout does not open the step again.
 */
const needsInvoice = (context) => context?.getData?.(INVOICE_REQUEST) === true || context?.getData?.("billing")?.needsInvoice === true

/** "Select your license": platform distribution of the license lines added without one */
const platformStep = {
  id: "platform",
  name: t("License"),
  title: t("Select Your License"),
  component: "sf-platform-step",
  buttonText: t("Continue"),
  requiresPaymentInfo: false,
  condition: ({ cart }) => linesNeedingPlatform(cart || []).length > 0,
}

/**
 * Billing / delivery address, skipped for digital-only carts. Before the
 * delivery step: the couriers price the delivery for it, and its name and
 * phone are who receives the parcel, at the door or at an office / locker
 */
const billingStep = {
  id: "billing",
  name: t("Address"),
  title: t("Billing address"),
  component: "sf-billing-step",
  buttonText: t("Continue"),
  requiresPaymentInfo: false,
  condition: (context) => !isDigitalOnly(context?.cart || []),
}

/** Company invoice details, opt-in (checkbox on the address step or "Need an invoice?" on the payment step) */
const invoiceDetailsStep = {
  id: "invoice-details",
  name: t("Invoice"),
  title: t("Invoice details"),
  component: "sf-invoice-step",
  buttonText: t("Continue"),
  requiresPaymentInfo: false,
  condition: (context) => needsInvoice(context),
  // in the step header only while asked for and not skipped ("No invoice")
  hideInProgress: (context) => !needsInvoice(context) || context?.getData?.("invoice-details")?.needsInvoice === false,
}

/** Delivery options + shipping cost, only when something has to be shipped — priced for the address given before */
const deliveryStep = {
  id: "delivery",
  name: t("Delivery"),
  title: t("Delivery"),
  component: "sf-delivery-step",
  buttonText: t("Continue"),
  requiresPaymentInfo: false,
  condition: ({ cart }) => hasPhysicalItems(cart || []),
}

const aldooSteps = [platformStep, billingStep, invoiceDetailsStep, deliveryStep]

export const registerAldooSteps = () => aldooSteps.forEach(registerStep)
